import { useState, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useSearchHistory } from '@/lib/store/searchHistory';

interface VulnerabilitySearchProps {
  onSearch: (query: string) => void;
  onClear: () => void;
  placeholder?: string;
  searchQuery?: string;
}

export function VulnerabilitySearch({ 
  onSearch, 
  onClear,
  placeholder,
  searchQuery = ''
}: VulnerabilitySearchProps) {
  const [query, setQuery] = useState(searchQuery);
  const inputRef = useRef<HTMLInputElement>(null);
  const addSearch = useSearchHistory(state => state.addSearch);
  const { searches } = useSearchHistory(state => ({ 
    searches: state.searches.filter(s => s.type === 'vulnerability') 
  }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query);
      addSearch(query, 'vulnerability');
    }
  };

  const handleClear = () => {
    setQuery('');
    onClear();
  };

  const handleRecentSearch = (searchQuery: string) => {
    setQuery(searchQuery);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-ibm-condensed sm:text-3xl text-foreground">
        Vulnerability Search
      </h2>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            type="search"
            placeholder={placeholder || "Search by product name..."}
            className="h-12 pl-12 text-base bg-background placeholder:text-muted-foreground w-full"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <Button type="submit" className="h-12">
          Search
        </Button>
      </form>

      {searchQuery && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Showing results for:</span>
          <span className="font-medium text-foreground">{searchQuery}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 hover:bg-destructive/10 hover:text-destructive"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {!searchQuery && searches.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground">Recent Searches</h3>
          <div className="flex flex-wrap gap-2">
            {searches.map((item) => (
              <button
                key={`${item.query}-${item.timestamp}`}
                onClick={() => handleRecentSearch(item.query)}
                className="px-3 py-1.5 text-sm rounded-full bg-accent hover:bg-accent/80 transition-colors"
              >
                {item.query}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}